import {
  ArrowDownCircleIcon,
  ArrowDownTrayIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  EnvelopeIcon,
  EyeIcon,
  EyeSlashIcon,
  InboxArrowDownIcon,
  LockClosedIcon,
  PencilSquareIcon,
  PlusIcon,
  PrinterIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { Overview } from 'components/Overview'
import { PlainInput2 } from 'components/PlainInput/PlainInput2'
import { SaveChanges } from 'components/SaveChanges'
import { AccountType, appApiUrl, InputType, SettingKey, userHasAccountType } from 'config'
import { usePermissions } from 'hooks/usePermissions'
import type { IDrawRequest } from 'pages/Borrower'
import { ScrollNavButtons } from 'pages/LoanSubmission/ScrollNavButtons'
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { toast } from 'react-toastify'
import { store } from 'reducers'
import {
  type BudgetAiExtractResult,
  applyBudgetTemplate,
  borrowerConfirmBudget,
  createBudgetTemplateList,
  deleteBudgetTemplate,
  downloadBudgetQA,
  getAdminConfig,
  getBudgetTemplate,
  getBudgetTemplateList,
  loanOverviewSection,
  openS3Document,
  orderSitewire,
  parseBudgetTemplateExcel,
  sendBudgetToBorrower,
  updateBudgetTemplate,
  updateBudgetTemplateItem,
  updateBudgetTemplateTitle,
} from 'services'
import {
  Button,
  ButtonGroup,
  Checkbox,
  Modal,
  PlainTable,
  Select,
  TextArea,
  Tooltip,
} from 'stories/components'
import { confirm, formatTime, isEmpty, openAuditLog, prompt, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  type UnmatchedBudgetAssignment,
  type UnmatchedBudgetItem,
  AddUnmatchedBudgetItemsModal,
} from './AddUnmatchedBudgetItemsModal'
import { BorroserEmailDialog } from './BorroserEmailDialog'
import { BudgetAiExtractModal } from './BudgetAiExtractModal'
import { isBudgetLineHidden, isBudgetLineUnused } from './budgetLineVisibility'
import { defaultInputs, normalizeDrawType, plainTextCostTypeItems } from './constants'
import { DownloadTypeDialog } from './DownloadTypeDialog'
import { DrawRequestsDialog } from './DrawRequestsDialog'
import {
  calcBudgetValues,
  convertContent,
  convertContent2,
  convertPercent,
  convertPercentContent,
  convertValue,
} from './logic'
import type { IBudgetData, IBudgetItem, IBudgetItemData, IBudgetItemValues, IBudgetSitewire } from './types'

export * from './BorrowerEditBudget'

/** What `GET/POST loan/budget` answers with: a label per form, its name, and whether one can be added. */
interface IBudgetFormList {
  addresses: Record<string, string>
  titles?: Record<string, string>
  addable: boolean
}

const BudgetScopeOfWork = ({
  isBorrower = false,
  noOverview = false,
  child = false,
}: {
  isBorrower?: boolean
  noOverview?: boolean
  child?: boolean
}) => {
  const [loading, setLoading] = useState(true)
  const [addressMap, setAddressMap] = useState<Record<string, string>>({})
  const [titleMap, setTitleMap] = useState<Record<string, string>>({})
  const [currentId, setCurrentId] = useState(0)
  const [addable, setAddable] = useState(true)

  const auth = useSelector((state: any) => state.auth)
  const formCount = useMemo(() => Object.keys(addressMap).length, [addressMap])

  const isAddressSelectable = !isBorrower || !auth.profile.budgetId

  useEffect(() => {
    if (isBorrower && auth.profile.budgetId) {
      setCurrentId(auth.profile.budgetId)
      setLoading(false)
    } else fetchList()
  }, [])

  const fetchList = () => {
    setLoading(true)

    getBudgetTemplateList()
      .then(async (list: IBudgetFormList) => {
        let { addresses, titles, addable } = list
        if (Object.keys(addresses).length == 0) {
          ;({ addresses, titles, addable } = await createBudgetTemplateList())
        }

        setAddressMap(addresses)
        setTitleMap(titles || {})
        setCurrentId(Number(Object.keys(addresses)[0]))
        setAddable(addable)
      })
      .finally(() => setLoading(false))
  }

  const onCreateForm = async () => {
    let title = ''

    // Every property on the loan already has a form, so this one is an extra budget on a property
    // that has one - an insurance claim scope beside the original rehab - and needs its own name,
    // because the address its tab would otherwise show is taken.
    if (!addable) {
      const result: any = await prompt('Name for the new budget form', { rows: 1, placeholder: 'Insurance Claim' })
      if (result === false) return

      title = `${result}`.trim()
      if (!title) {
        toast('Please name the new budget form.', { type: 'error' })
        return
      }
    }

    setLoading(true)

    createBudgetTemplateList(title)
      .then(({ addresses, titles, addable }: IBudgetFormList) => {
        setAddressMap(addresses)
        setTitleMap(titles || {})
        const ids = Object.keys(addresses)
        setCurrentId(Number(ids[ids.length - 1]))
        setAddable(addable)
      })
      .finally(() => setLoading(false))
  }

  const onRenameForm = async () => {
    const result: any = await prompt('Name for this budget form - leave it blank to show the property address', {
      value: titleMap[currentId] || '',
      rows: 1,
    })
    if (result === false) return

    setLoading(true)

    updateBudgetTemplateTitle(currentId, `${result}`.trim())
      .then(({ addresses, titles }: IBudgetFormList) => {
        setAddressMap(addresses)
        setTitleMap(titles || {})
      })
      .finally(() => setLoading(false))
  }

  const onDeleteForm = async () => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure to remove this form?
        <br />
        <span className="text-gray-600">Form: {addressMap[currentId]}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    setLoading(true)
    await deleteBudgetTemplate(currentId)
    setCurrentId(0)
    await fetchList()
  }

  return (
    <div className="px-2 my-6">
      {!noOverview && !child && <Overview title="Budget & Scope of Work" hasBackButton={!isBorrower} />}

      <div className={`relative max-w-screen-2xl m-auto ${child ? '' : 'shadow1 rounded mb-4 px-4 py-6'}`}>
        <LayoutLoading show={loading} />
        <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
          <span>Budget & Scope of Work</span>
        </h2>

        {isAddressSelectable && (
          <div className="flex items-center justify-between mb-4 flex-4">
            {!!formCount && <ButtonGroup title={addressMap} value={`${currentId}`} onChange={(v) => setCurrentId(v)} />}
            {!isBorrower && !!formCount && (
              <div className="flex items-center flex-4">
                <Button onClick={onCreateForm}>Add</Button>
                <Button onClick={onRenameForm}>Rename</Button>
                <Button color="red" disabled={formCount == 1} onClick={onDeleteForm}>
                  Delete
                </Button>
              </div>
            )}
          </div>
        )}

        <BudgetScopeOfWorkItem key={currentId} id={currentId} isBorrower={isBorrower} />
      </div>
      <ScrollNavButtons />
    </div>
  )
}

const sitewireLineAmount = (values: IBudgetItemValues) => {
  const lender = values?.rehabCostLenderApproved
  if (lender !== null && lender !== undefined && String(lender) !== '') {
    return Number(String(lender).replace(/,/g, '')) || 0
  }
  return Number(String(values?.rehabCostBorrowerEstimated || 0).replace(/,/g, '')) || 0
}

/** Same lines the order sends: hidden lines are left out, and so is a blank line with no amount. */
const countSitewireLines = (budget?: IBudgetData) => {
  if (!budget?.items) return 0
  let count = 0
  budget.items.forEach((item) => {
    Object.keys(item.data || {}).forEach((key) => {
      const values = item.data[key]
      if (!values || values.hidden) return
      const description = String(values.description || '').trim()
      if (!description && !sitewireLineAmount(values)) return
      count += 1
    })
  })
  return count
}

const BudgetScopeOfWorkItem = ({ id, isBorrower = false }: { id: number; isBorrower?: boolean }) => {
  const { auth, borrower, constructionReserve } = useSelector((state: any) => ({
    auth: state.auth,
    borrower: state.borrower,
    constructionReserve: state.loan?.constructionReserve,
  }))

  const [loanStatus, setLoanStatus] = useState<string>('')
  const [servicingStatus, setServicingStatus] = useState<string>('')
  const [isDownloadTypeDialog, showDownloadTypeDialog] = useState(false)

  const servicingPipeline = !!servicingStatus
  const isFunded = ['funded'].includes(loanStatus) || servicingPipeline

  const alwaysEditable = userHasAccountType(auth.profile, [
    AccountType.ADMIN,
    AccountType.UW_MANAGER,
    AccountType.UNDERWRITER,
    AccountType.LOCK_DESK,
    AccountType.DRAW_SPECIALIST,
    AccountType.FEASIBILITY_ANALYST,
  ])

  const fileInputRef = useRef<HTMLInputElement>(null)
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [submitModal, setSubmitModal] = useState(false)
  const [histories, setHistories] = useState<any>([])
  const [inputs, setInputs] = useState<Record<string, InputType>>({})
  const [budget, setBudget] = useState<IBudgetData>()
  const [total, setTotal] = useState<IBudgetItemData>({})
  const [loading, setLoading] = useState(false)
  const [isEditing, setEditing] = useState('')
  const [budgetItemMapping, setBudgetItemMapping] = useState<Record<string, string>>({})
  const [drawRequests, setDrawRequests] = useState<IDrawRequest[]>([])
  const [showDrawRequests, setShowDrawRequests] = useState<{
    value: string
    title: string
    // The line's own keys, not its labels: the dialog reads each request's entry for this line to
    // get the amount and narrative the borrower put against it.
    lineValue: string
    lineKey: string
    requests: IDrawRequest[]
  } | null>(null)
  const [rehabFinancedPercent, setRehabFinancedPercent] = useState(1)
  const [templateKey, setTemplateKey] = useState('')
  const [templateOptions, setTemplateOptions] = useState<Record<string, string>>({})
  const [isBorrowerEmailDialog, showBorrowerEmailDialog] = useState(false)
  const [submitForm, setSubmitForm] = useState(false)
  const [inputFieldChanged, setInputFieldChanged] = useState(false)
  const [isAiExtractOpen, setIsAiExtractOpen] = useState(false)
  const [budgetAiEnabled, setBudgetAiEnabled] = useState(false)
  const [pendingUnmatched, setPendingUnmatched] = useState<UnmatchedBudgetItem[]>([])
  const [isAddUnmatchedOpen, setIsAddUnmatchedOpen] = useState(false)
  const [occupiedLineKeys, setOccupiedLineKeys] = useState<string[]>([])
  // Reveals the hidden lines in place so they can be put back. A view setting, not part of the
  // budget, so it is never sent to the borrower's copy of this screen.
  const [showHidden, setShowHidden] = useState(false)
  const [sitewire, setSitewire] = useState<IBudgetSitewire | null>(null)
  const [orderingSitewire, setOrderingSitewire] = useState(false)

  const stateData = store.getState()
  const urlParams: any = useParams()
  const { loanNumber } = urlParams

  const { hasPermission } = usePermissions()

  const brokerProfile = useMemo(() => {
    return !hasPermission('Loan.BudgetScope.Override') || isBorrower
  }, [isBorrower])

  const isAdmin = hasPermission('Loan.BudgetScope.Override')

  const canAiExtract = !isBorrower && hasPermission('Loan.BudgetScopeWork.AIExtract') && budgetAiEnabled

  useEffect(() => {
    if (!hasPermission('Loan.BudgetScopeWork.AIExtract')) {
      setBudgetAiEnabled(false)
      return
    }
    getAdminConfig(SettingKey.GLOBAL_SETTINGS).then((settings: any) => {
      setBudgetAiEnabled(!!settings?.budgetAiAutofillEnabled)
    })
  }, [])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        init()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [])

  const onDownload = () => {
    showDownloadTypeDialog(true)
  }

  /**
   * Switch this budget form to another configured budget template. The backend keeps amounts for
   * every line the new template also has, so the confirmation only warns about the rest.
   */
  const onChangeTemplate = async (nextKey: string) => {
    if (!nextKey || nextKey === templateKey) return

    // Built into a variable first: passing JSX straight into a call breaks the production build.
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Rebuild this budget from the <span className="text-gray-600">{templateOptions[nextKey]}</span> template?
        <br />
        <span className="text-[16px]">
          Amounts already entered are kept for every line item the new template also has. Line items it does not have
          are removed.
        </span>
      </div>
    )
    const ok = await confirm(content)
    if (!ok) return

    setLoading(true)
    try {
      const { carriedOver, dropped } = await applyBudgetTemplate(id, nextKey)
      toast(
        `Budget rebuilt from "${templateOptions[nextKey]}". ${carriedOver} line item${
          carriedOver === 1 ? '' : 's'
        } carried over, ${dropped} removed.`,
        { type: 'info' },
      )
      await init()
    } catch (e: any) {
      toast(e?.message || 'Failed to change the budget template.', { type: 'error' })
      setLoading(false)
    }
  }

  const init = async () => {
    if (id == 0) return

    let newInputs = cloneDeep(defaultInputs())

    setLoading(true)
    const res = await getBudgetTemplate(id)
    setLoading(false)

    const newBudget = calcBudgetValues(res.budget.data) as Record<string, any>
    if (newBudget.drawType) newBudget.drawType = normalizeDrawType(newBudget.drawType)
    Object.keys(newInputs).forEach((key) => {
      if (newBudget[key] !== undefined) newInputs[key].value = String(newBudget[key])
    })

    onUpdateTotal(newBudget as IBudgetData)

    // Build complete item mapping including custom items
    const completeItemMapping = { ...res.itemMapping }
    newBudget.items.forEach((item: any) => {
      Object.keys(item.data).forEach((key) => {
        // If item has a custom title and the key is not in the default mapping, add it
        if (item.data[key].title && !completeItemMapping[key]) {
          completeItemMapping[key] = item.data[key].title
        }
      })
    })

    setBudgetItemMapping(completeItemMapping)
    setTemplateKey(res.templateKey || '')
    setTemplateOptions(res.templateOptions || {})
    setDrawRequests(res.drawRequests)
    setBudget(newBudget as IBudgetData)
    setHistories(res.budget.histories)
    setSitewire(res.budget.sitewire || null)
    setRehabFinancedPercent(res.rehabFinancedPercent)
    setInputs(newInputs)

    let allLocked = true
    newBudget.items.map((item: any) => {
      Object.keys(item.data).map((key) => {
        const data = item.data[key]
        if (data.lockData !== true) {
          allLocked = false
        }
      })
    })
    let lastAction = ''
    res.budget.histories?.map((history: any) => {
      lastAction = history.action
    })
    setSubmitForm(!allLocked || lastAction !== 'Lock All Items')
  }

  useEffect(() => {
    loanOverviewSection().then((data) => {
      setLoanStatus(data.loanStatus)
      setServicingStatus(data.servicingStatus)
    })
  }, [])

  useEffect(() => {
    init()
  }, [id])

  useEffect(() => {
    if (!Object.keys(inputs).length) return
    setInputs((prev) => {
      const updated = { ...prev }
      updated.constructionAmountFinanced = {
        ...prev.constructionAmountFinanced,
        value: constructionReserve != null ? String(constructionReserve) : '',
      } as InputType
      return updated
    })
  }, [constructionReserve, Object.keys(inputs).length])

  const onUpdateTotal = (data: IBudgetData | undefined = budget) => {
    const newTotal = cloneDeep(total)

    newTotal['total'] = {
      rehabCostAlreadyPrepaid: 0,
      rehabCostBorrowerEstimated: 0,
      rehabCostLenderApproved: 0,
      lenderDisbursedAmount: 0,
      borrowerResponsibility: 0,
      costType: '',
      description: '',
    }

    data?.items.forEach((item) => {
      const newTotalValue: IBudgetItemValues = {
        rehabCostAlreadyPrepaid: 0,
        rehabCostBorrowerEstimated: 0,
        rehabCostLenderApproved: 0,
        lenderDisbursedAmount: 0,
        borrowerResponsibility: 0,
        costType: '',
        description: '',
      }

      Object.keys(item.data).forEach((key) => {
        const itemValues = item.data[key]

        newTotalValue.rehabCostBorrowerEstimated += isNaN(itemValues.rehabCostBorrowerEstimated)
          ? 0
          : Number(itemValues.rehabCostBorrowerEstimated)

        if (item.value === 'miscellaneous' && plainTextCostTypeItems.includes(key)) {
        } else {
          newTotalValue.rehabCostAlreadyPrepaid += isNaN(itemValues.rehabCostAlreadyPrepaid)
            ? 0
            : Number(itemValues.rehabCostAlreadyPrepaid)
        }

        newTotalValue.rehabCostLenderApproved += isNaN(itemValues.rehabCostLenderApproved)
          ? 0
          : Number(itemValues.rehabCostLenderApproved)
        newTotalValue.borrowerResponsibility += Number(itemValues.borrowerResponsibility)
        newTotalValue.lenderDisbursedAmount += Number(itemValues.lenderDisbursedAmount || 0)
      })

      newTotal['total'].rehabCostBorrowerEstimated += Number(newTotalValue.rehabCostBorrowerEstimated)
      newTotal['total'].rehabCostAlreadyPrepaid += Number(newTotalValue.rehabCostAlreadyPrepaid)
      newTotal['total'].rehabCostLenderApproved += Number(newTotalValue.rehabCostLenderApproved)
      newTotal['total'].borrowerResponsibility += Number(newTotalValue.borrowerResponsibility)
      newTotal['total'].lenderDisbursedAmount += Number(newTotalValue.lenderDisbursedAmount)

      newTotal[item.value] = newTotalValue
    })

    setTotal(newTotal)

    return newTotal
  }

  const onChangeItemValues = async (
    value: string,
    group: string,
    label: string,
    key: string,
    data?: IBudgetItemValues,
  ) => {
    if (!budget) return
    const nBudget = cloneDeep(budget)
    let newInputs = cloneDeep(inputs)

    setEditing('')

    try {
      let needSave = false

      const index = nBudget?.items.findIndex((item) => item.value === group)
      if (index === -1) return

      const item = nBudget.items[index]
      const itemData = item.data[label]
      switch (key) {
        case 'rehabCostBorrowerEstimated':
        case 'rehabCostLenderApproved':
        case 'rehabCostAlreadyPrepaid':
        case 'borrowerResponsibility':
        case 'lenderDisbursedAmount':
          if (key == 'lenderDisbursedAmount' && data) {
            const maxValue = data.rehabCostLenderApproved * rehabFinancedPercent
            if (Number(value) > maxValue) {
              value = String(data.rehabCostLenderApproved * rehabFinancedPercent)
              confirm(
                <p className="text-sm">
                  The amount entered exceeds the maximum pro rated amount financed. Based on the amount of the budget
                  financed being <span className="font-semibold">${rehabFinancedPercent * 100}%</span>, the maximum
                  lender disbursed amount should not exceed{' '}
                  <span className="font-semibold">{convertContent(maxValue)}</span>. Please confirm you wish to proceed
                  with <span className="font-semibold">{convertContent(value)}</span> for this field.
                </p>,
                {
                  titleYes: false,
                  titleNo: 'Ok',
                },
              )
            }
          }
          if (itemData[key] !== ((isEmpty(value) ? null : Number(value)) as number)) {
            needSave = true
            itemData[key] = (isEmpty(value) ? null : Number(value)) as number
            if (
              key == 'rehabCostLenderApproved' &&
              itemData.rehabCostLenderApproved == itemData.rehabCostBorrowerEstimated &&
              !itemData.lockData
            )
              itemData.lockData = true
          }
          break
        case 'costType':
          if (itemData.costType !== value) {
            needSave = true
            itemData.costType = value
          }
          break
        case 'title':
          if (itemData[key] !== value) {
            needSave = true
            itemData[key] = value
            // Update the budget item mapping when title changes
            setBudgetItemMapping((prev) => ({
              ...prev,
              [label]: value,
            }))
          }
          break
        case 'description':
          if (itemData[key] !== value) {
            needSave = true
            itemData[key] = value
          }
          break
        default:
          return
      }
      if (!needSave) return

      // Working on a line puts it back on the form for good. The flag is cleared by the server on
      // the same write that carries the value - a second request for it races that one and loses -
      // so this only keeps the screen in step until the next read.
      if (itemData.hidden) itemData.hidden = false

      let newTotal = onUpdateTotal(nBudget)

      const newBudget = calcBudgetValues(nBudget as IBudgetData) as Record<string, any>
      newTotal = onUpdateTotal(newBudget as IBudgetData)

      updateBudgetData('item', group, label, key, (itemData as Record<string, any>)[key])

      Object.keys(newInputs).forEach((key) => {
        newInputs[key].value = String(newBudget[key])
      })

      newInputs['constructionBudget'].value = convertContent(newTotal['total'].rehabCostBorrowerEstimated)

      setTotal(newTotal)
      setBudget(newBudget as IBudgetData)
      setInputs(newInputs)
    } catch (error) {
      console.log(error)
    }
  }

  const onChangeDescription = (value: string, group: string, label: string) => {
    const nBudget = cloneDeep(budget)

    setEditing('')

    nBudget?.items.forEach((item) => {
      if (item.value === group) {
        if (item.data[label].description !== value) item.data[label].description = value
      }
    })

    setBudget(nBudget)
    setInputFieldChanged(true)
  }

  const onChangeNote = (value: string, group: string, label: string) => {
    const nBudget = cloneDeep(budget)

    nBudget?.items.forEach((item) => {
      if (item.value === group) {
        if (item.data[label].note !== value) item.data[label].note = value
      }
    })

    setBudget(nBudget)
    setInputFieldChanged(true)
  }

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    const nBudget = cloneDeep(budget) as Record<string, any>

    if (['constructionManagementFee'].includes(key)) nBudget[key] = isEmpty(value) ? null : removeComma(value)
    else nBudget[key] = value

    const newBudget = calcBudgetValues(nBudget as IBudgetData) as Record<string, any>
    const newTotal = onUpdateTotal(newBudget as IBudgetData)

    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = String(newBudget[key])
    })

    setTotal(newTotal)
    setBudget(newBudget as IBudgetData)
    setInputs(newInputs)
    setInputFieldChanged(true)
  }

  const onBlur = async (key: string) => {
    if (!inputFieldChanged) return
    setInputFieldChanged(false)

    await updateBudgetData('input', '', '', key, (budget as any)[key])

    if (key == 'BathroomCount') init()
  }

  const onBlurItem = (group: string, label: string, key: string) => {
    if (!inputFieldChanged) return
    setInputFieldChanged(false)

    if (!budget) return

    const index = budget.items.findIndex((item) => item.value === group)
    if (index === -1) return

    const item = budget.items[index]

    updateBudgetData('item', group, label, key, (item.data[label] as Record<string, any>)[key])
  }

  const showBudgetItemHistory = (group: string, itemKey: string, objKey: string) => {
    const options = {
      table: 'Budget',
      field: `${group}-${itemKey}-${objKey}`,
      keys: {
        field: `${group}-${itemKey}-${objKey}`,
      },
    }
    openAuditLog(options)
  }

  const showHistory = (key: string) => {
    const options = {
      table: 'Budget',
      field: key,
      keys: {
        field: key,
      },
    }
    openAuditLog(options)
  }

  const editInternalDesc = async (group: string, intDes: string = '', key: string) => {
    const value: any = await prompt(budgetItemMapping[key] + ' Internal Note', {
      value: intDes,
    })
    if (value === false) return
    if (value === intDes) return

    const nBudget: any = cloneDeep(budget)
    const index = nBudget.items.findIndex((item: any) => item.value === group)
    const item = nBudget.items[index]
    item.data[key].intDes = value
    setBudget(nBudget as IBudgetData)
    updateBudgetData('item', group, key, 'intDes', value)
  }

  const editNarrativeNote = async () => {
    const value: any = await prompt('Narrative Note', {
      value: budget?.narrativeNote,
    })
    if (value === false) return
    if (value === budget?.narrativeNote) return
    const nBudget = cloneDeep(budget) as Record<string, any>
    nBudget.narrativeNote = value
    setBudget(nBudget as IBudgetData)
    updateBudgetData('input', '', '', 'narrativeNote', value)
  }

  const onOrderSitewire = async () => {
    const address = String(budget?.address || '').trim()
    if (!address) {
      toast('This budget has no address.', { type: 'error' })
      return
    }

    const lineCount = countSitewireLines(budget)
    const updating = !!sitewire?.propertyId
    const content = (
      <div className="text-gray-600 mb-4 text-left text-[16px]">
        {updating ? 'Update this budget in Sitewire?' : 'Order this budget in Sitewire?'}
        <div className="mt-3 text-[15px]">
          <div>Address: {address}</div>
          <div>Budget lines: {lineCount}</div>
        </div>
      </div>
    )
    const ok = await confirm(content, { titleYes: updating ? 'Update Sitewire' : 'Order Sitewire', titleNo: 'Cancel' })
    if (!ok) return

    setOrderingSitewire(true)
    try {
      const res = await orderSitewire(id)
      setSitewire(res.sitewire || null)
      if (res.histories) setHistories(res.histories)
      if (res.message) toast(res.message, { type: 'info' })
    } catch {
      const refreshed = await getBudgetTemplate(id).catch(() => null)
      if (refreshed?.budget) {
        setSitewire(refreshed.budget.sitewire || null)
        setHistories(refreshed.budget.histories)
      }
    } finally {
      setOrderingSitewire(false)
    }
  }

  const onSendToBorrower = async () => {
    let requests: any = []
    budget?.items.map((item) => {
      Object.keys(item.data)
        .sort()
        .map((key) => {
          const { highlight, note } = item.data[key]
          if (highlight) {
            requests.push({
              title: budgetItemMapping[key],
              note: note,
            })
          }
        })
    })

    const content = (
      <div className="mb-4 text-left">
        <div
          className="bg-gray-100 border border-gray-400 text-gray-700 px-4 py-3 rounded relative mb-4 text-[14px]"
          role="alert"
        >
          <div className="mb-2 font-semibold">The borrower will have to update the items that have been checked:</div>
          {requests.map((request: any, index: number) => {
            return (
              <div className="md:col-span-1" key={index}>
                <div>- {request.title}</div>
                {request.note && <div className="ml-3 border border-gray-200 p-2 italic mb-4">{request.note}</div>}
              </div>
            )
          })}
        </div>
      </div>
    )

    if (requests.length > 0) {
      const result = await confirm(content, { titleYes: 'Continue' })
      if (!result) return
    }

    showBorrowerEmailDialog(true)
  }

  const onBorrowerEmailDialogClose = async (result: boolean, emails: Record<string, string>, note: string = '') => {
    if (!result) {
      showBorrowerEmailDialog(false)
      return
    }
    await sendBudgetToBorrower(id, emails, note)
    showBorrowerEmailDialog(false)
  }

  const errors = useMemo(() => {
    if (!inputs.narrative) return []

    let rlt: any = []
    budget?.items.map((item) => {
      Object.keys(item.data)
        .sort()
        .map((key) => {
          const data = item.data[key]
          const costBorrowerEstimatedError = data.highlight && removeComma(data.rehabCostBorrowerEstimated) === 0

          if (costBorrowerEstimatedError) {
            rlt.push({ title: budgetItemMapping[key], note: data.note })
          }
        })
    })
    if (inputs.narrative.value?.trim().length > 0) {
    } else {
      rlt.push({
        title:
          'Please provide a narrative of what is transpiring in the project. Include details such as what exact work is being performed inside and outside of the structure. Is square footage being added? Will room count change? Etc.',
      })
    }
    return rlt
  }, [budget?.items, inputs.narrative?.value])

  const isAllLockData = useMemo(() => {
    if (!budget?.items) return false
    const hasUnlockData = !!budget?.items.filter((item) => {
      return !!Object.keys(item.data).filter((itemKey) => {
        return !item.data[itemKey].lockData
      }).length
    }).length
    return !hasUnlockData
  }, [budget?.items])

  const isAllHighlight = useMemo(() => {
    if (!budget?.items?.length) return false
    return budget.items.every((item) => Object.keys(item.data).every((itemKey) => item.data[itemKey].highlight))
  }, [budget?.items])

  const isGroupAllHighlight = (group: string) => {
    const section = budget?.items.find((item) => item.value === group)
    if (!section?.data || !Object.keys(section.data).length) return false
    return Object.keys(section.data).every((itemKey) => section.data[itemKey].highlight)
  }

  const onChangeCheckBoxAllHighlight = async (value: boolean, group?: string) => {
    if (!budget) return
    setEditing('')
    const nBudget = cloneDeep(budget)
    nBudget.items.forEach((item) => {
      if (group && item.value !== group) return
      Object.keys(item.data).forEach((itemKey) => {
        item.data[itemKey].highlight = value
        if (value) item.data[itemKey].lockData = false
      })
    })
    setBudget(nBudget)
    setLoading(true)
    try {
      await updateBudgetTemplate(id, { budget: nBudget, isBorrower })
    } finally {
      setLoading(false)
    }
  }

  const onConfirmAndSubmit = async () => {
    setLoading(true)

    await borrowerConfirmBudget(id)

    const nBudget = cloneDeep(budget)
    nBudget?.items.forEach((item) => {
      Object.keys(item.data).forEach((itemKey) => {
        item.data[itemKey]['lockData'] = true
      })
    })
    setBudget(nBudget)

    setLoading(false)

    setSubmitModal(false)

    setSubmitForm(false)
  }

  const onChangeCheckBoxAllItem = (objKey: 'lockData', value: boolean) => {
    setEditing('')
    const data = {
      type: 'itemAll',
      group: '',
      key: '',
      objKey,
      value,
      isBorrower,
    }

    const nBudget = cloneDeep(budget)
    nBudget?.items.forEach((item) => {
      Object.keys(item.data).forEach((itemKey) => {
        item.data[itemKey][objKey] = value
      })
    })
    setBudget(nBudget)

    setLoading(true)

    updateBudgetTemplateItem(id, data)
      .catch((error) => console.log(error))
      .finally(() => setLoading(false))
  }

  const onChangeCheckBoxItem = async (group: string, itemKey: string, objKey: 'highlight' | 'lockData') => {
    if (!budget) return
    const nBudget = cloneDeep(budget)

    const index = nBudget.items.findIndex((item) => item.value === group)
    if (index === -1) return

    const item = nBudget.items[index]
    if (item.data[itemKey]) item.data[itemKey][objKey] = !item.data[itemKey][objKey]
    if (objKey === 'highlight' && item.data[itemKey][objKey]) {
      item.data[itemKey]['lockData'] = false
    }
    setBudget(nBudget)
    updateBudgetData('item', group, itemKey, objKey, item.data[itemKey][objKey])
  }

  const onAddItemToSection = async (group: string) => {
    if (!budget) return

    // Prompt user to enter the title for the new item
    const itemTitle: any = await prompt('Enter Item Title', {
      value: '',
      placeholder: 'e.g., Custom Flooring, Additional Work, etc.',
    })

    // If user cancels or enters empty title, don't add the item
    if (itemTitle === false || !itemTitle || String(itemTitle).trim().length === 0) {
      return
    }

    const nBudget = cloneDeep(budget)

    const index = nBudget.items.findIndex((item) => item.value === group)
    if (index === -1) return

    const item = nBudget.items[index]

    // Generate a unique key for the new item using timestamp
    const newItemKey = `custom_${group}_${Date.now()}`

    // Get the default cost type from existing items in this section, or default to 'Hard costs'
    const existingKeys = Object.keys(item.data)
    let defaultCostType = 'Hard costs'
    if (existingKeys.length > 0) {
      const firstItem = item.data[existingKeys[0]]
      defaultCostType = firstItem.costType || 'Hard costs'
    }

    // Add the new item to the section with the user-provided title
    item.data[newItemKey] = {
      rehabCostBorrowerEstimated: NaN,
      rehabCostLenderApproved: NaN,
      rehabCostAlreadyPrepaid: NaN,
      lenderDisbursedAmount: NaN,
      borrowerResponsibility: NaN,
      costType: defaultCostType,
      description: '',
      title: String(itemTitle).trim(),
      highlight: false,
      lockData: false,
    }

    // Update the budget item mapping to include the new key with the custom title
    setBudgetItemMapping((prev) => ({
      ...prev,
      [newItemKey]: String(itemTitle).trim(),
    }))

    const newBudget = calcBudgetValues(nBudget as IBudgetData) as Record<string, any>
    const newTotal = onUpdateTotal(newBudget as IBudgetData)

    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = String(newBudget[key])
    })

    setTotal(newTotal)
    setBudget(newBudget as IBudgetData)
    setInputs(newInputs)

    // Save the updated budget to the backend
    setLoading(true)
    await updateBudgetTemplate(id, { budget: newBudget, isBorrower })
    setLoading(false)

    toast('New item added successfully', { type: 'success' })
  }

  const onDeleteItem = async (group: string, itemKey: string) => {
    if (!budget) return

    // Confirm deletion
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Are you sure you want to delete this item?
        <br />
        <span className="text-gray-600 font-semibold">{budgetItemMapping[itemKey]}</span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const nBudget = cloneDeep(budget)

    const index = nBudget.items.findIndex((item) => item.value === group)
    if (index === -1) return

    const item = nBudget.items[index]

    // Delete the item from the section
    delete item.data[itemKey]

    // Remove from budget item mapping
    setBudgetItemMapping((prev) => {
      const newMapping = { ...prev }
      delete newMapping[itemKey]
      return newMapping
    })

    const newBudget = calcBudgetValues(nBudget as IBudgetData) as Record<string, any>
    const newTotal = onUpdateTotal(newBudget as IBudgetData)

    let newInputs = cloneDeep(inputs)
    Object.keys(newInputs).forEach((key) => {
      newInputs[key].value = String(newBudget[key])
    })

    newInputs['constructionBudget'].value = convertContent(newTotal['total'].rehabCostBorrowerEstimated)

    setTotal(newTotal)
    setBudget(newBudget as IBudgetData)
    setInputs(newInputs)

    // Save the updated budget to the backend
    setLoading(true)
    await updateBudgetTemplate(id, { budget: newBudget, isBorrower })
    setLoading(false)

    toast('Item deleted successfully', { type: 'info' })
  }

  // Line keys this project has not used, per section. A line qualifies only while it is empty, so a
  // figure entered later makes it count again - and the lender is never looking at a budget whose
  // totals include a row that is not on the page.
  const unusedLineKeys = useMemo(() => {
    const result: Record<string, string[]> = {}
    budget?.items.forEach((item) => {
      result[item.value] = Object.keys(item.data).filter((key) => {
        if (item.data[key].hidden) return false
        if (!isBudgetLineUnused(item.data[key])) return false
        // A line someone has asked a draw against is in use whatever its columns say.
        return !drawRequests?.some((request) => request.types?.some((v) => v.value === item.value && v.key === key))
      })
    })
    return result
  }, [budget?.items, drawRequests])

  const unusedLineCount = useMemo(
    () => Object.values(unusedLineKeys).reduce((count, keys) => count + keys.length, 0),
    [unusedLineKeys],
  )

  /** Lines that are in use or already hidden - what would be left if the unused ones went. */
  const keptLineCount = useMemo(() => {
    if (!budget?.items) return 0
    const total = budget.items.reduce((count, item) => count + Object.keys(item.data).length, 0)
    return total - unusedLineCount
  }, [budget?.items, unusedLineCount])

  const hiddenLineCount = useMemo(() => {
    if (!budget?.items) return 0
    return budget.items.reduce(
      (count, item) => count + Object.keys(item.data).filter((key) => isBudgetLineHidden(item.data[key])).length,
      0,
    )
  }, [budget?.items])

  /** The lines of a section that are on the form right now, in the order the table prints them. */
  const visibleLineKeys = (item: IBudgetItem) =>
    Object.keys(item.data)
      .sort()
      .filter((key) => showHidden || !isBudgetLineHidden(item.data[key]))

  /** Take one line off the form, or put it back. */
  const onToggleHideItem = async (group: string, itemKey: string, hidden: boolean) => {
    if (!budget) return
    const nBudget = cloneDeep(budget)

    const index = nBudget.items.findIndex((item) => item.value === group)
    if (index === -1) return
    if (!nBudget.items[index].data[itemKey]) return

    nBudget.items[index].data[itemKey].hidden = hidden
    setBudget(nBudget)

    await updateBudgetData('item', group, itemKey, 'hidden', hidden)
  }

  /** Clear every unused line off the form in one go - the state a blank template starts in. */
  const onHideUnusedLines = async () => {
    if (!budget || !unusedLineCount) return

    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Hide {unusedLineCount} unused budget {unusedLineCount === 1 ? 'line' : 'lines'}?
        <br />
        <span className="text-[15px]">
          They are left off this form, its PDF and its Excel, and off the copy the borrower sees. Nothing is deleted -
          Show hidden lines puts any of them back.
        </span>
      </div>
    )
    if (!(await confirm(content))) return

    const nBudget = cloneDeep(budget)
    nBudget.items.forEach((item) => {
      ;(unusedLineKeys[item.value] || []).forEach((key) => {
        if (item.data[key]) item.data[key].hidden = true
      })
    })

    const hiddenNow = unusedLineCount
    setBudget(nBudget)
    setShowHidden(false)

    setLoading(true)
    try {
      await updateBudgetTemplate(id, { budget: nBudget, isBorrower })
      toast(`${hiddenNow} unused ${hiddenNow === 1 ? 'line' : 'lines'} hidden`, { type: 'success' })
    } finally {
      setLoading(false)
    }
  }

  /** Put every hidden line back on the form. */
  const onShowAllLines = async () => {
    if (!budget || !hiddenLineCount) return

    const nBudget = cloneDeep(budget)
    nBudget.items.forEach((item) => {
      Object.keys(item.data).forEach((key) => {
        if (item.data[key].hidden) item.data[key].hidden = false
      })
    })

    setBudget(nBudget)

    setLoading(true)
    try {
      await updateBudgetTemplate(id, { budget: nBudget, isBorrower })
    } finally {
      setLoading(false)
    }
  }

  const updateBudgetData = (type: 'input' | 'item', group: string, key: string, objKey: string, value: any) => {
    setEditing('')
    const data = {
      type,
      group,
      key,
      objKey,
      value,
      isBorrower,
    }

    return new Promise((resolve) => {
      setLoading(true)
      updateBudgetTemplateItem(id, data)
        .then(() => resolve(true))
        .catch((error) => console.log(error))
        .finally(() => setLoading(false))
    })
  }

  const importExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files![0]

    setLoading(true)
    const json = { file }
    const jsonData: string[][] = await parseBudgetTemplateExcel(json)

    if (!budget) {
      setLoading(false)
      return
    }

    try {
      const newBudget = cloneDeep(budget)

      // The importer reads this grid's own layout: a header row whose first cell is "No", with the
      // column titles below. A budget kept in the lender's own format has no such row, and this
      // used to return silently — the spinner stopped, nothing changed, and nothing said why, which
      // reads as "the file was rejected". Say what was expected instead.
      const titleRow = jsonData.find((item) => item[0] === 'No')
      if (!titleRow) {
        setLoading(false)
        toast(
          'That file does not match the budget template, so nothing was imported. Export Excel gives you the ' +
            'template to fill in, or use AI Extract to pull a budget out of a document in your own format.',
          { type: 'error', autoClose: false },
        )
        return
      }
      const titleIndex = titleRow.findIndex((item) => item === 'Description')
      const borrowerEstimatedIndex = titleRow.findIndex(
        (item) => item?.replaceAll('\r\n', ' ') === 'Rehab Cost Borrower Estimated',
      )
      const lenderApprovedIndex = titleRow.findIndex(
        (item) => item?.replaceAll('\r\n', ' ') === 'Rehab Cost Lender Approved',
      )
      const alreadyPrepaidByBorrowerIndex = titleRow.findIndex(
        (item) => item?.replaceAll('\r\n', ' ') === 'Rehab Cost Already Prepaid By Borrower',
      )
      const costTypeIndex = titleRow.findIndex((item) => item?.replaceAll('\r\n', ' ') === 'Cost Type')
      const descriptionIndex = titleRow.findIndex((item) => item?.replaceAll('\r\n', ' ') === 'Work to Be Performed')

      if (
        borrowerEstimatedIndex === -1 ||
        alreadyPrepaidByBorrowerIndex === -1 ||
        costTypeIndex === -1 ||
        descriptionIndex === -1
      ) {
        let unknowColumn = ''
        if (borrowerEstimatedIndex === -1) unknowColumn = 'Rehab Cost Borrower Estimated'
        else if (alreadyPrepaidByBorrowerIndex === -1) unknowColumn = 'Rehab Cost Already Prepaid By Borrower'
        else if (costTypeIndex === -1) unknowColumn = 'Cost Type'
        else if (descriptionIndex === -1) unknowColumn = 'Work to Be Performed'
        toast(`Excel template doesn't have '${unknowColumn}' coulumn`, { type: 'error' })
        setLoading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      if (isFunded && lenderApprovedIndex === -1) {
        toast("Excel template doesn't have 'Rehab Cost Lender Approved' coulumn", { type: 'error' })
        setLoading(false)
        if (fileInputRef.current) fileInputRef.current.value = ''
        return
      }

      let addedBathMore = false
      let isBath = false
      let targetIndex = -1
      let totalImportableRows = 0
      let totalUnknownRows = 0
      let unknownRows: Array<Array<any>> = []

      jsonData.forEach((item) => {
        if (!item.length) return
        if (item[0] === null || item[0] === '') {
          targetIndex = newBudget.items.findIndex((d) => d.label.trim() === item[titleIndex])

          if (item[titleIndex]?.toLowerCase().includes('bath')) isBath = true
          else isBath = false

          // Add Bath section
          if (isBath && targetIndex === -1) {
            addedBathMore = true
            const insertIndex = newBudget.items.findIndex((d) => d.value === `bath${newBudget.BathroomCount}`) + 1

            const newBudgetItem: IBudgetItem = {
              label: `BATH ${Number(newBudget.BathroomCount) + 1}`,
              value: `bath${Number(newBudget.BathroomCount) + 1}`,
              data: {},
            }
            newBudget.BathroomCount = `${Number(newBudget.BathroomCount) + 1}`
            newBudget.items.splice(insertIndex, 0, newBudgetItem)
            targetIndex = insertIndex
          }
        }
        if (typeof item[0] === 'number' && targetIndex !== -1) {
          const title = item[titleIndex]
          let key =
            Object.keys(budgetItemMapping)[Object.values(budgetItemMapping).findIndex((v) => v.trim() === title.trim())]

          // Show incorrect titles on modal after imported
          if (!key) {
            unknownRows.push(item)
            totalUnknownRows++
            console.log('Skipped the unknown title row:', title)
            return
          }

          if (isBath && key.toLowerCase().includes('kitchen')) {
            let idx = -1
            Object.values(budgetItemMapping).forEach((k, id) => {
              if (k === title) idx = id
            })

            if (idx !== -1) key = Object.keys(budgetItemMapping)[idx]
          }

          if (!key) {
            unknownRows.push(item)
            totalUnknownRows++
            console.log('Skipped the unknown title row:', title)
            return
          }

          totalImportableRows++

          if (!newBudget.items[targetIndex].data[key]) {
            newBudget.items[targetIndex].data[key] = {
              borrowerResponsibility: NaN,
              costType: '',
              description: '',
              highlight: false,
              lockData: false,
              rehabCostAlreadyPrepaid: NaN,
              rehabCostBorrowerEstimated: NaN,
              rehabCostLenderApproved: NaN,
              lenderDisbursedAmount: NaN,
            }
          }

          if (!brokerProfile || !newBudget.items[targetIndex].data[key].lockData) {
            if (!brokerProfile && isFunded)
              newBudget.items[targetIndex].data[key].rehabCostLenderApproved = item[lenderApprovedIndex]
                ? removeComma(item[lenderApprovedIndex])
                : NaN

            if (!isBorrower) newBudget.items[targetIndex].data[key].costType = item[costTypeIndex]

            newBudget.items[targetIndex].data[key].rehabCostBorrowerEstimated = item[borrowerEstimatedIndex]
              ? removeComma(item[borrowerEstimatedIndex])
              : NaN
            newBudget.items[targetIndex].data[key].rehabCostAlreadyPrepaid = item[alreadyPrepaidByBorrowerIndex]
              ? removeComma(item[alreadyPrepaidByBorrowerIndex])
              : NaN
            newBudget.items[targetIndex].data[key].description = item[descriptionIndex]
          }
        }
      })

      let html = (
        <>
          <div
            className="my-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative text-[15px] flex items-center"
            role="alert"
          >
            {totalImportableRows} / {totalImportableRows + totalUnknownRows} rows are importable.
          </div>
          {!!totalUnknownRows && (
            <>
              <div className="text-left text-red-500 font-semibold">
                We can't find below {totalUnknownRows} Rows, Are you sure you want to import?
              </div>
              <div className="max-h-120 overflow-auto">
                <table className="table relative w-full text-sm">
                  <thead className="text-gray-700 bg-gray-100 dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      {titleRow.map((v, i) => (
                        <th key={i} className={`p-2 ${v == 'Description' && 'w-[20%]'}`}>
                          {v}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {unknownRows.map((row, index: number) => (
                      <tr key={index} className={`${index % 2 == 1 && 'bg-slate-100'}`}>
                        {titleRow.map((v, i: number) => (
                          <td key={i} className="text-left p-1">
                            <span className={`h-16 overflow-auto flex ${i < titleRow.length - 1 && 'items-center'}`}>
                              {row[i] === undefined || row[i] === null ? '' : row[i]}
                            </span>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )
      const confirmResult: any = await confirm(
        html,
        { maxWidth: totalUnknownRows ? '6xl' : '2xl', titleYes: 'Import' },
        2,
      )
      if (confirmResult === false) {
        if (fileInputRef.current) fileInputRef.current.value = ''
        setLoading(false)
        return
      }

      let newTotal = onUpdateTotal(newBudget)

      const nBudget = calcBudgetValues(newBudget)
      newTotal = onUpdateTotal(nBudget)

      let newInputs = cloneDeep(inputs)

      Object.keys(newInputs).forEach((key) => {
        newInputs[key].value = String((nBudget as any)[key])
      })

      newInputs['constructionBudget'].value = convertContent(newTotal['total'].rehabCostBorrowerEstimated)

      const data = {
        budget: nBudget,
        isBorrower: isBorrower,
      }

      await updateBudgetTemplate(id, data)

      if (addedBathMore) await updateBudgetData('input', '', '', 'BathroomCount', (nBudget as any)['BathroomCount'])

      setTotal(newTotal)
      setBudget(nBudget)
      setInputs(newInputs)

      toast('Excel data has been successfully imported', { type: 'info' })
    } catch (error) {
      console.log('Imporing excel error: ', error)
    }

    if (fileInputRef.current) fileInputRef.current.value = ''
    setLoading(false)
  }

  const applyAiExtract = async (result: BudgetAiExtractResult) => {
    if (!budget) return

    const newBudget = cloneDeep(budget)
    let applied = 0
    const occupied: string[] = []

    if (result.narrative) {
      newBudget.narrative = result.narrative
    }

    result.items.forEach((item) => {
      if (!item.sectionValue || !item.lineKey) return
      const sectionIndex = newBudget.items.findIndex((s) => s.value === item.sectionValue)
      if (sectionIndex === -1) return
      const section = newBudget.items[sectionIndex]
      if (!section.data[item.lineKey]) return
      if (brokerProfile && section.data[item.lineKey].lockData) return

      let changed = false
      if (item.rehabCostBorrowerEstimated != null && item.rehabCostBorrowerEstimated !== 0) {
        section.data[item.lineKey].rehabCostBorrowerEstimated = item.rehabCostBorrowerEstimated
        changed = true
      }
      if (item.rehabCostAlreadyPrepaid != null && item.rehabCostAlreadyPrepaid !== 0) {
        section.data[item.lineKey].rehabCostAlreadyPrepaid = item.rehabCostAlreadyPrepaid
        changed = true
      }
      if (item.costType && !isBorrower) {
        section.data[item.lineKey].costType = item.costType
        changed = true
      }
      if (item.description) {
        section.data[item.lineKey].description = item.description
        changed = true
      }
      if (changed) {
        applied += 1
        occupied.push(`${item.sectionValue}::${item.lineKey}`)
      }
    })

    if (result.unmatched?.length) {
      setPendingUnmatched(result.unmatched)
      setOccupiedLineKeys(occupied)
    }

    if (!applied && !result.narrative) {
      if (result.unmatched?.length) {
        toast('No matched budget items to apply. Use “Assign unmatched items” to map the remaining lines.', {
          type: 'info',
        })
      } else {
        toast('No matched budget items could be applied.', { type: 'warning' })
      }
      return
    }

    setLoading(true)
    try {
      let newTotal = onUpdateTotal(newBudget)
      const nBudget = calcBudgetValues(newBudget)
      newTotal = onUpdateTotal(nBudget)

      const newInputs = cloneDeep(inputs)
      Object.keys(newInputs).forEach((key) => {
        newInputs[key].value = String((nBudget as any)[key] ?? '')
      })
      newInputs['constructionBudget'].value = convertContent(newTotal['total'].rehabCostBorrowerEstimated)
      if (result.narrative) newInputs.narrative.value = result.narrative

      await updateBudgetTemplate(id, { budget: nBudget, isBorrower })

      setTotal(newTotal)
      setBudget(nBudget as IBudgetData)
      setInputs(newInputs)

      const parts: string[] = []
      if (applied) parts.push(`${applied} line${applied === 1 ? '' : 's'}`)
      if (result.narrative) parts.push('narrative')
      toast(`Applied ${parts.join(' and ')} from the document.`, { type: 'info' })
    } catch (error) {
      console.log('Budget AI extract apply error: ', error)
      toast('Failed to apply extracted budget items.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const openAddUnmatchedItems = (unmatched: UnmatchedBudgetItem[]) => {
    setPendingUnmatched(unmatched)
    setIsAddUnmatchedOpen(true)
  }

  const applyUnmatchedAssignments = async (assignments: UnmatchedBudgetAssignment[]) => {
    if (!budget) return

    const newBudget = cloneDeep(budget)
    let applied = 0
    // Its own counter: `applied` only rises when a line changes, so two new lines added in the same
    // millisecond would otherwise be handed the same key and the second would overwrite the first.
    let created = 0
    const mappingUpdates: Record<string, string> = {}

    let addedSections = 0
    for (const assignment of assignments) {
      let sectionIndex = newBudget.items.findIndex((s) => s.value === assignment.sectionValue)
      // A form organised into its own sections brings headings this budget has never had. Create
      // them on the way in, or a whole imported budget collapses into whatever section happened to
      // be closest.
      if (sectionIndex === -1 && assignment.newSectionLabel) {
        newBudget.items.push({
          label: assignment.newSectionLabel,
          value: assignment.sectionValue,
          data: {},
        })
        sectionIndex = newBudget.items.length - 1
        addedSections += 1
      }
      if (sectionIndex === -1) continue
      const section = newBudget.items[sectionIndex]

      let lineKey = assignment.lineKey
      if (!lineKey) {
        lineKey = `custom_${assignment.sectionValue}_${Date.now()}_${created}`
        created += 1
        const existingKeys = Object.keys(section.data)
        let defaultCostType = 'Hard costs'
        if (existingKeys.length > 0) {
          defaultCostType = section.data[existingKeys[0]].costType || 'Hard costs'
        }
        const title = (assignment.title || 'Untitled item').trim()
        section.data[lineKey] = {
          rehabCostBorrowerEstimated: NaN,
          rehabCostLenderApproved: NaN,
          rehabCostAlreadyPrepaid: NaN,
          lenderDisbursedAmount: NaN,
          borrowerResponsibility: NaN,
          costType: assignment.costType || defaultCostType,
          description: '',
          title,
          highlight: false,
          lockData: false,
        }
        mappingUpdates[lineKey] = title
      }

      if (!section.data[lineKey]) continue
      if (brokerProfile && section.data[lineKey].lockData) continue

      let changed = false
      if (assignment.rehabCostBorrowerEstimated != null && assignment.rehabCostBorrowerEstimated !== 0) {
        section.data[lineKey].rehabCostBorrowerEstimated = assignment.rehabCostBorrowerEstimated
        changed = true
      }
      if (assignment.rehabCostAlreadyPrepaid != null && assignment.rehabCostAlreadyPrepaid !== 0) {
        section.data[lineKey].rehabCostAlreadyPrepaid = assignment.rehabCostAlreadyPrepaid
        changed = true
      }
      if (assignment.costType && !isBorrower) {
        section.data[lineKey].costType = assignment.costType
        changed = true
      }
      if (assignment.description) {
        section.data[lineKey].description = assignment.description
        changed = true
      }
      if (assignment.title && lineKey.startsWith('custom_')) {
        section.data[lineKey].title = assignment.title
        mappingUpdates[lineKey] = assignment.title
        changed = true
      }
      if (changed || mappingUpdates[lineKey]) applied += 1
    }

    if (!applied) {
      toast('No unmatched items could be assigned.', { type: 'warning' })
      return
    }

    if (Object.keys(mappingUpdates).length) {
      setBudgetItemMapping((prev) => ({ ...prev, ...mappingUpdates }))
    }

    setLoading(true)
    try {
      let newTotal = onUpdateTotal(newBudget)
      const nBudget = calcBudgetValues(newBudget)
      newTotal = onUpdateTotal(nBudget)

      const newInputs = cloneDeep(inputs)
      Object.keys(newInputs).forEach((key) => {
        newInputs[key].value = String((nBudget as any)[key] ?? '')
      })
      newInputs['constructionBudget'].value = convertContent(newTotal['total'].rehabCostBorrowerEstimated)

      await updateBudgetTemplate(id, { budget: nBudget, isBorrower })

      setTotal(newTotal)
      setBudget(nBudget as IBudgetData)
      setInputs(newInputs)
      setPendingUnmatched([])
      setOccupiedLineKeys([])
      const sectionNote = addedSections
        ? ` in ${addedSections} new section${addedSections === 1 ? '' : 's'}`
        : ''
      toast(`Assigned ${applied} unmatched item${applied === 1 ? '' : 's'}${sectionNote}.`, { type: 'success' })
    } catch (error) {
      console.log('Budget AI unmatched assign error: ', error)
      toast('Failed to assign unmatched budget items.', { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  const onExportQA = async (type: 'pdf' | 'docx') => {
    const highlightItems: Record<string, any>[] = []
    budget?.items.forEach((v) => {
      Object.keys(v.data).forEach((key) => {
        const item = v.data[key]
        if (item.highlight)
          highlightItems.push({
            title: budgetItemMapping[key],
            note: item.note,
            description: item.description,
            rehabCostBorrowerEstimated: item.rehabCostBorrowerEstimated,
          })
      })
    })
    const narrativeNote = inputs.narrative.value || ''
    setLoading(true)
    await downloadBudgetQA(highlightItems, narrativeNote, type, `Budget & Scope QA - ${loanNumber}.${type}`)
    setLoading(false)
  }

  const renderInputs = useCallback(() => {
    return (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-4">
        {Object.keys(inputs).map((key: string) => {
          let input = inputs[key]
          if (input.visible === false) return null
          return (
            <div className={`input md:col-span-1`} key={key}>
              <RenderInput
                input={{ ...input, disabled: isBorrower ? true : false, history: !input.readOnly && !isBorrower }}
                Key={key}
                onChange={onChange}
                onBlur={() => onBlur(key)}
                showHistory={showHistory}
              />
            </div>
          )
        })}
      </div>
    )
  }, [inputs, isBorrower, showHistory])

  const renderBudgetItemChild = (item: IBudgetItem, index: number, totalIndex: number, key: string, id: number) => {
    const data = item.data[key]
    const costBorrowerEstimatedError = data.highlight && removeComma(data.rehabCostBorrowerEstimated) === 0

    // Only on screen because Show hidden lines is on, so it is dimmed and offers its way back.
    const isHiddenLine = isBudgetLineHidden(data)
    const isHideable = !isHiddenLine && (unusedLineKeys[item.value] || []).includes(key)

    let cellEditable = true
    if (data.lockData && !alwaysEditable) {
      cellEditable = false
    }

    const foundDrawRequests = drawRequests
      ? drawRequests.filter((v) => !!v.types.filter((v) => item.value == v.value && v.key == key).length)
      : []
    const hasDrawRequests = !!foundDrawRequests.length

    const AuditLogIcon = ({
      field,
      className = '',
      inFlex = false,
    }: {
      field: string
      className?: string
      inFlex?: boolean
    }) => (
      <div
        className={`${
          inFlex ? '' : 'absolute top-[2px] right-[2px] hidden group-hover:block'
        } z-10 cursor-pointer hover-shadow1 p-1 rounded
        ${className}`}
        onClick={(e) => {
          if (field === 'lockData') e.stopPropagation()
          showBudgetItemHistory(item.value, key, field)
        }}
      >
        <ClockIcon className="w-4 h-4" />
      </div>
    )

    const indexTd = (
      <td className={`px-2 py-0 border ${data.lockData && 'bg-gray-200/75'} ${data.highlight && 'bg-yellow-100'}`}>
        {totalIndex}
        {data.lockData && (
          <span>
            <br />
            <LockClosedIcon className="w-4 h-4 ml-1"></LockClosedIcon>
          </span>
        )}
      </td>
    )

    // For custom items, prefer data.title, otherwise use budgetItemMapping
    let title = data.title || budgetItemMapping[key] || ''
    if (!budgetItemMapping[key] && !data.title) console.log('Error', key)
    const matches = title.match(/Other(\s[\w|\s]+): Please Describe/)
    let isOtherRow = !!matches || !!data.title // Custom items are also editable like "Other" rows
    if (matches) {
      title = data.title || title.replace(matches[1], '')
    }
    if (!cellEditable) isOtherRow = false

    const showDrawRequestButton = (
      <div
        className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded"
        onClick={() =>
          setShowDrawRequests({
            value: item.label,
            title,
            lineValue: item.value,
            lineKey: key,
            requests: foundDrawRequests,
          })
        }
      >
        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
      </div>
    )

    // What the borrower has asked for against this line, totalled from their requests' own line
    // entries. Shown to staff and borrower alike, and only when there is something to show, so a
    // line nobody has asked for stays clean.
    const requestedAmount = Number(data.borrowerRequestedAmount || 0)
    // And what the lender approved against it, which is the figure that actually gets funded. Shown
    // under the requested one so the gap between the two is visible on the line it belongs to.
    const approvedAmount = Number(data.borrowerApprovedAmount || 0)
    const requestedNote = (requestedAmount > 0 || approvedAmount > 0) && (
      <>
        {requestedAmount > 0 && (
          <div
            className="text-[11px] opacity-70 whitespace-nowrap"
            title="Total the borrower has requested against this line"
          >
            Requested {convertContent(requestedAmount)}
          </div>
        )}
        {approvedAmount > 0 && (
          <div
            className="text-[11px] opacity-70 whitespace-nowrap"
            title="Total the lender has approved against this line"
          >
            Approved {convertContent(approvedAmount)}
          </div>
        )}
      </>
    )

    const typeTd = (
      <td
        className={`relative group px-2 py-1 border text-left ${data.highlight && 'bg-yellow-100'} ${
          plainTextCostTypeItems.includes(key) ? 'bg-gray-100 font-bold' : ''
        }`}
      >
        {isBorrower ? (
          <div>
            {title}
            {requestedNote}
            <div className="absolute top-[2px] right-[2px] items-center gap-2">
              {hasDrawRequests && showDrawRequestButton}
            </div>
          </div>
        ) : (
          <div className="">
            <div className="flex items-center">
              <div className="w-auto">
                <Checkbox
                  color="gray"
                  id={`highlight-${key}`}
                  title={isOtherRow ? '' : title}
                  key={key}
                  value={data.highlight as boolean}
                  onChange={() => {
                    !isBorrower && cellEditable && onChangeCheckBoxItem(item.value, key, 'highlight')
                  }}
                />
              </div>
              {isOtherRow && (
                <PlainInput2
                  value={title}
                  content={title}
                  type="text"
                  className="italic -mt-1 -ml-1"
                  isEditing={cellEditable && isEditing === `${index}-${id}-${item.value}-${key}-title`}
                  editable={cellEditable}
                  onChange={(value: string) => onChangeItemValues(value, item.value, key, 'title')}
                />
              )}
            </div>
            {requestedNote}
            {!isBorrower && cellEditable && (
              <div className="absolute top-[2px] right-[2px] items-center gap-2 flex">
                {/* Take an unused line off the form, or put a hidden one back */}
                {(isHideable || isHiddenLine) && (
                  <div
                    className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded hidden group-hover:block"
                    onClick={() => onToggleHideItem(item.value, key, !isHiddenLine)}
                    title={isHiddenLine ? 'Put this line back on the budget' : 'Hide this unused line'}
                  >
                    {isHiddenLine ? (
                      <EyeIcon className="w-4 h-4 text-shade-blue" />
                    ) : (
                      <EyeSlashIcon className="w-4 h-4" />
                    )}
                  </div>
                )}

                {/* Delete button for custom items */}
                {key?.startsWith('custom_') && (
                  <div
                    className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded hover:bg-red-100 hidden group-hover:block"
                    onClick={() => onDeleteItem(item.value, key)}
                    title="Delete this item"
                  >
                    <TrashIcon className="w-4 h-4 text-red-600"></TrashIcon>
                  </div>
                )}

                <AuditLogIcon field="highlight" className="relative" />

                <div
                  className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded hidden group-hover:block"
                  onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-titleNotes`)}
                >
                  <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
                </div>

                {hasDrawRequests && showDrawRequestButton}
              </div>
            )}
            {isEditing === `${index}-${id}-${item.value}-${key}-titleNotes` && (
              <TextArea
                value={data.note}
                rows={2}
                onChange={(value: string) => onChangeNote(value, item.value, key)}
                onBlur={() => onBlurItem(item.value, key, 'note')}
                className="text-[13.5px] p-[2px]"
              />
            )}
          </div>
        )}
        {data.note && data.note?.length > 0 && (
          <div className="italic mt-1 ml-2 text-right text-[13.5px] break-words w-96">- {data.note}</div>
        )}
      </td>
    )

    const lockTd = alwaysEditable && (
      <td
        className="relative group px-2 py-1 border cursor-pointer"
        onClick={() => onChangeCheckBoxItem(item.value, key, 'lockData')}
      >
        <Checkbox
          color="gray"
          id={`lockData-${key}`}
          title={''}
          key={key}
          value={data.lockData as boolean}
          onChange={() => {}}
        />
        <AuditLogIcon field="lockData" />
      </td>
    )

    return (
      <tr key={`${index}-${id}`} className={isHiddenLine ? 'opacity-50 italic' : ''}>
        {indexTd}
        {typeTd}

        {/* Rehab Cost Borrower Estimated */}
        <td
          className={`relative group px-2 py-0 border ${cellEditable ? 'cursor-pointer' : 'cursor-not-allowed'} ${
            isBorrower ? 'bg-green-50' : ''
          }`}
          onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-rehabCostBorrowerEstimated`)}
        >
          <PlainInput2
            value={convertValue(data.rehabCostBorrowerEstimated)}
            content={convertContent(data.rehabCostBorrowerEstimated)}
            type="number"
            isEditing={cellEditable && isEditing === `${index}-${id}-${item.value}-${key}-rehabCostBorrowerEstimated`}
            editable={cellEditable}
            onChange={(value: string) => onChangeItemValues(value, item.value, key, 'rehabCostBorrowerEstimated')}
          />
          {costBorrowerEstimatedError && (
            <span
              className={`text-red-900 px-4 ${
                isEditing !== `${index}-${id}-${item.value}-${key}-rehabCostBorrowerEstimated` && 'py-2 bg-red-200'
              } rounded`}
            >
              Required
            </span>
          )}
          <AuditLogIcon field="rehabCostBorrowerEstimated" />
        </td>

        {/* Lender Financed Amount */}
        <td className={`px-2 py-0 border`}>{convertContent(data.rehabCostBorrowerEstimated * rehabFinancedPercent)}</td>

        {/* Rehab Cost Lender Approved Percentage */}
        {servicingPipeline && (
          <td
            className={`relative group ${
              brokerProfile ? 'cursor-not-allowed' : 'cursor-pointer'
            } px-2 py-0 border cursor-pointer ${
              removeComma(data.rehabCostLenderApproved) !== 0 &&
              removeComma(data.rehabCostLenderApproved) != removeComma(data.rehabCostBorrowerEstimated) &&
              'border-l-4 border-l-red-400'
            }`}
            onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-rehabCostLenderApprovedPercent`)}
          >
            <PlainInput2
              value={convertPercent(data.rehabCostBorrowerEstimated, data.rehabCostLenderApproved)}
              content={convertPercent(data.rehabCostBorrowerEstimated, data.rehabCostLenderApproved, true)}
              type="number"
              isEditing={
                cellEditable &&
                isEditing === `${index}-${id}-${item.value}-${key}-rehabCostLenderApprovedPercent` &&
                !brokerProfile
              }
              editable={alwaysEditable}
              onChange={(value: string) => {
                onChangeItemValues(
                  ((data.rehabCostBorrowerEstimated * Math.min(Number(value), 100)) / 100).toFixed(2),
                  item.value,
                  key,
                  'rehabCostLenderApproved',
                )
              }}
            />
          </td>
        )}

        {/* Rehab Cost Lender Approved */}
        {servicingPipeline && (
          <td
            className={`relative group ${
              brokerProfile ? 'cursor-not-allowed' : 'cursor-pointer'
            } px-2 py-0 border cursor-pointer ${
              removeComma(data.rehabCostLenderApproved) !== 0 &&
              removeComma(data.rehabCostLenderApproved) != removeComma(data.rehabCostBorrowerEstimated) &&
              'border-l-4 border-l-red-400'
            }`}
            onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-rehabCostLenderApproved`)}
          >
            <PlainInput2
              value={convertValue(data.rehabCostLenderApproved)}
              content={convertContent(data.rehabCostLenderApproved)}
              type="number"
              isEditing={
                cellEditable &&
                isEditing === `${index}-${id}-${item.value}-${key}-rehabCostLenderApproved` &&
                !brokerProfile
              }
              editable={alwaysEditable}
              onChange={(value: string) => onChangeItemValues(value, item.value, key, 'rehabCostLenderApproved')}
            />
            {alwaysEditable &&
              convertValue(data.rehabCostBorrowerEstimated).length > 0 && ( // !isBorrower && cellEditable
                <div className="absolute top-[2px] right-[2px] hidden group-hover:flex gap-1">
                  <div
                    className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded"
                    onClick={(e) => {
                      e.stopPropagation()
                      onChangeItemValues(
                        convertValue(data.rehabCostBorrowerEstimated),
                        item.value,
                        key,
                        'rehabCostLenderApproved',
                      )
                    }}
                  >
                    <ClipboardDocumentCheckIcon className="w-[15px] h-[15px]"></ClipboardDocumentCheckIcon>
                  </div>
                  <AuditLogIcon field="rehabCostLenderApproved" inFlex />
                </div>
              )}
          </td>
        )}

        {/* Lender Disbursed Amount */}
        {isFunded && (
          <td
            className={`relative group ${
              brokerProfile ? 'cursor-not-allowed' : 'cursor-pointer'
            } px-2 py-0 border cursor-pointer`}
            onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-lenderDisbursedAmount`)}
          >
            <PlainInput2
              value={convertValue(data.lenderDisbursedAmount)}
              content={convertContent(data.lenderDisbursedAmount)}
              type="number"
              isEditing={
                cellEditable &&
                isEditing === `${index}-${id}-${item.value}-${key}-lenderDisbursedAmount` &&
                !brokerProfile
              }
              editable={alwaysEditable}
              onChange={(value: string) => onChangeItemValues(value, item.value, key, 'lenderDisbursedAmount', data)}
            />
            {alwaysEditable &&
              convertValue(data.rehabCostLenderApproved).length > 0 && ( // !isBorrower && cellEditable
                <div className="absolute top-[2px] right-[2px] hidden group-hover:flex gap-1">
                  <Tooltip message="Fill Max Value">
                    <div
                      className="relative z-10 cursor-pointer hover-shadow1 p-1 rounded"
                      onClick={(e) => {
                        e.stopPropagation()
                        onChangeItemValues(
                          convertValue(data.rehabCostLenderApproved * rehabFinancedPercent),
                          item.value,
                          key,
                          'lenderDisbursedAmount',
                          data,
                        )
                      }}
                    >
                      <ClipboardDocumentCheckIcon className="w-[15px] h-[15px]"></ClipboardDocumentCheckIcon>
                    </div>
                  </Tooltip>
                  <AuditLogIcon field="lenderDisbursedAmount" inFlex />
                </div>
              )}
          </td>
        )}

        {/* Remaining Escrow — approved less disbursed. Derived, so it is never edited directly. */}
        {servicingPipeline && isFunded && (
          <td className="px-2 py-0 border cursor-not-allowed">
            <span className="text-black font-variation-settings-600">
              {convertContent(data.rehabCostLenderApproved - data.lenderDisbursedAmount)}
            </span>
          </td>
        )}

        {/* Rehab Cost already Prepaid by Borrower */}
        <td
          className={`relative group px-2 py-0 border ${cellEditable ? 'cursor-pointer' : 'cursor-not-allowed'} ${
            isBorrower ? 'bg-green-50' : ''
          }`}
          onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-rehabCostAlreadyPrepaid`)}
        >
          <PlainInput2
            value={convertValue(data.rehabCostAlreadyPrepaid)}
            content={convertContent(data.rehabCostAlreadyPrepaid)}
            type="number"
            isEditing={cellEditable && isEditing === `${index}-${id}-${item.value}-${key}-rehabCostAlreadyPrepaid`}
            editable={cellEditable}
            onChange={(value: string) => onChangeItemValues(value, item.value, key, 'rehabCostAlreadyPrepaid')}
          />
          <AuditLogIcon field="rehabCostAlreadyPrepaid" />
        </td>

        {/* Borrower Responsibility of Budget Item */}
        <td
          className="px-2 py-0 border cursor-not-allowed"
          onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-borrowerResponsibility`)}
        >
          <span className="text-black font-variation-settings-600">{convertContent2(data.borrowerResponsibility)}</span>
        </td>

        {/* Cost Type */}
        <td
          className="relative group px-2 py-1 border cursor-pointer"
          onClick={() => setEditing(`${index}-${id}-${item.value}-${key}-costType`)}
        >
          {isBorrower ? (
            <span tabIndex={0} className="text-black font-variation-settings-600">
              {data.costType}
            </span>
          ) : plainTextCostTypeItems.includes(key) ? (
            <PlainInput2
              value={data.costType}
              content={data.costType}
              isEditing={cellEditable && isEditing === `${index}-${id}-${item.value}-${key}-costType`}
              editable={isBorrower ? false : cellEditable}
              onChange={(value: string) => onChangeItemValues(value, item.value, key, 'costType')}
            />
          ) : (
            <Select
              id="cost-type"
              size={3}
              options={['Soft costs', 'Hard costs']}
              value={data.costType}
              hasDefaultOption={true}
              disabled={!cellEditable}
              className="min-w-[110px] mb-[-16px] border-none"
              onChange={(value: string) => onChangeItemValues(value, item.value, key, 'costType')}
            />
          )}
          <AuditLogIcon field="costType" />
        </td>

        <td className="relative group px-2 py-1 border cursor-pointer min-w-[200px]">
          <TextArea
            value={data.description}
            rows={2}
            disabled={!cellEditable}
            onChange={(value: string) => onChangeDescription(value, item.value, key)}
            onBlur={() => onBlurItem(item.value, key, 'description')}
            className="text-[13.5px]"
          />
          {!brokerProfile && data.intDes && (
            <div className="text-right italic text-[13.5px] break-all">{data.intDes}</div>
          )}
          {!isBorrower && !brokerProfile && (
            <div className="absolute top-[2px] right-[2px] hidden group-hover:flex gap-1">
              <AuditLogIcon field="description" inFlex={true} />
              <div
                className="cursor-pointer hover-shadow1 p-1 rounded"
                onClick={() => editInternalDesc(item.value, data.intDes, key)}
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </div>
            </div>
          )}
        </td>

        {lockTd}
      </tr>
    )
  }

  const renderBudgetItem = (item: IBudgetItem, index: number, totalIndex: number) => {
    if (item.value.startsWith('bath')) {
      const bathCount = Number(inputs['BathroomCount']?.value || 3)
      const bathIndex = Number(item.value.replace('bath', ''))
      if (bathCount < bathIndex) return null
    }

    // A section whose every line has been hidden goes with them, header and all - leaving a lone
    // $0.00 heading behind is exactly the clutter hiding is meant to clear.
    const lineKeys = visibleLineKeys(item)
    if (!lineKeys.length) return null

    const totalItem = total[item.value]
    return (
      <Fragment key={index}>
        <tr className="bg-gray-100 font-semibold">
          <td className="px-2 py-1 border"></td>
          <td className="px-2 py-1 border text-left">
            {!isBorrower ? (
              <Checkbox
                className="w-auto"
                color="gray"
                id={`highlight-${item.value}`}
                title={item.label}
                value={isGroupAllHighlight(item.value)}
                onChange={() => onChangeCheckBoxAllHighlight(!isGroupAllHighlight(item.value), item.value)}
                additionalElements={
                  <button
                    onClick={() => onAddItemToSection(item.value)}
                    className="hover:bg-gray-200 rounded p-1 transition-colors"
                    title="Add new item to this section"
                  >
                    <PlusIcon className="w-4 h-4 text-shade-blue" />
                  </button>
                }
              />
            ) : (
              <span>{item.label}</span>
            )}
          </td>
          <td className="px-2 py-1 border">{convertContent(totalItem.rehabCostBorrowerEstimated)}</td>
          <td className="px-2 py-1 border">
            {convertContent(totalItem.rehabCostBorrowerEstimated * rehabFinancedPercent)}
          </td>
          {servicingPipeline && (
            <>
              <td className="px-2 py-1 border">
                {convertPercentContent(
                  (totalItem.rehabCostLenderApproved / totalItem.rehabCostBorrowerEstimated) * 100,
                )}
              </td>
              <td className="px-2 py-1 border">{convertContent(totalItem.rehabCostLenderApproved)}</td>
            </>
          )}
          {isFunded && <td className="px-2 py-1 border">{convertContent(totalItem.lenderDisbursedAmount)}</td>}
          {servicingPipeline && isFunded && (
            <td className="px-2 py-1 border">
              {convertContent(totalItem.rehabCostLenderApproved - totalItem.lenderDisbursedAmount)}
            </td>
          )}
          <td className="px-2 py-1 border">{convertContent(totalItem.rehabCostAlreadyPrepaid)}</td>
          <td className="px-2 py-1 border">{convertContent2(totalItem.borrowerResponsibility)}</td>
          <td className="px-2 py-1 border"></td>
          <td className="px-2 py-1 border"></td>
          {alwaysEditable && <td className="px-2 py-1 border"></td>}
        </tr>

        {lineKeys.map((key, id) => renderBudgetItemChild(item, index, totalIndex++, key, id))}
      </Fragment>
    )
  }

  const renderBudgetTotal = () => {
    const {
      rehabCostBorrowerEstimated,
      rehabCostLenderApproved,
      lenderDisbursedAmount,
      rehabCostAlreadyPrepaid,
      borrowerResponsibility,
    } = total['total']

    return (
      <tr className="bg-gray-100 font-semibold">
        <td className="px-2 py-1 border"></td>
        <td className="px-2 py-1 border text-left uppercase">
          {!isBorrower ? (
            <Checkbox
              className="w-auto"
              color="gray"
              id="highlight-all"
              title="Grand Total"
              fontClass="uppercase"
              value={isAllHighlight}
              onChange={() => onChangeCheckBoxAllHighlight(!isAllHighlight)}
            />
          ) : (
            <span>Grand Total</span>
          )}
        </td>
        <td className="px-2 py-1 border">{convertContent(rehabCostBorrowerEstimated)}</td>
        <td className="px-2 py-1 border">{convertContent(rehabCostBorrowerEstimated * rehabFinancedPercent)}</td>
        {servicingPipeline && (
          <>
            <td className="px-2 py-1 border">
              {convertPercentContent((rehabCostLenderApproved / rehabCostBorrowerEstimated) * 100)}
            </td>
            <td className="px-2 py-1 border">{convertContent(rehabCostLenderApproved)}</td>
          </>
        )}
        {isFunded && <td className="px-2 py-1 border">{convertContent(lenderDisbursedAmount)}</td>}
        {servicingPipeline && isFunded && (
          <td className="px-2 py-1 border">{convertContent(rehabCostLenderApproved - lenderDisbursedAmount)}</td>
        )}
        <td className="px-2 py-1 border">{convertContent(rehabCostAlreadyPrepaid)}</td>
        <td className="px-2 py-1 border">{convertContent2(borrowerResponsibility)}</td>
        <td className="px-2 py-1 border"></td>
        <td className="px-2 py-1 border"></td>
        {alwaysEditable && (
          <td className="px-2 py-1 border" onClick={() => onChangeCheckBoxAllItem('lockData', !isAllLockData)}>
            <Checkbox color="gray" id={`lockData-all`} title={''} value={isAllLockData} onChange={() => {}} />
          </td>
        )}
      </tr>
    )
  }

  const renderBudget = useCallback(() => {
    let headers = [
      'No',
      'Description',
      'Rehab Cost Borrower Estimated',
      'Lender Financed Amount',
      'Rehab Cost Lender Approved Percentage',
      'Rehab Cost Lender Approved',
      'Lender Disbursed Amount',
      'Remaining Escrow',
      `Rehab Cost already Prepaid by Borrower`,
      'Borrower Responsibility of Budget Item',
      'Cost Type',
      'Work to be performed',
      'Lock Data',
    ]

    if (!servicingPipeline) {
      let index = headers.indexOf('Rehab Cost Lender Approved Percentage')
      if (index != -1) headers.splice(index, 1)
      index = headers.indexOf('Rehab Cost Lender Approved')
      if (index != -1) headers.splice(index, 1)
    }

    if (!isFunded) {
      let index = headers.indexOf('Lender Disbursed Amount')
      if (index != -1) headers.splice(index, 1)
    }

    // What is still held back: approved less what has already gone out. It is derived from the two
    // columns either side of it, so it only appears when both of those do.
    if (!servicingPipeline || !isFunded) {
      const index = headers.indexOf('Remaining Escrow')
      if (index != -1) headers.splice(index, 1)
    }

    if (!alwaysEditable) {
      const index = headers.indexOf('Lock Data')
      headers.splice(index, 1)
    }

    let totalIndex = 1
    return (
      <div>
        <div className="flex justify-between items-center gap-4 my-4">
          <div
            className="bg-sky-100 border border-sky-400 text-sky-700 px-4 py-3 rounded relative text-[15px]"
            role="alert"
          >
            The Budget must only include work that has not been completed as part of the project. Any completed work,
            must not be included.
          </div>
          <div className="flex items-center gap-2">
            {/* Nothing to clear away until some of the budget is filled in. */}
            {!isBorrower && unusedLineCount > 0 && keptLineCount > 0 && (
              <Button link color="blue" onClick={onHideUnusedLines}>
                {`Hide ${unusedLineCount} unused ${unusedLineCount === 1 ? 'line' : 'lines'}`}
              </Button>
            )}
            {!isBorrower && hiddenLineCount > 0 && (
              <Button link color="blue" onClick={() => setShowHidden(!showHidden)}>
                {showHidden
                  ? 'Hide them again'
                  : `Show ${hiddenLineCount} hidden ${hiddenLineCount === 1 ? 'line' : 'lines'}`}
              </Button>
            )}
            {!isBorrower && showHidden && hiddenLineCount > 0 && (
              <Button link color="blue" onClick={onShowAllLines}>
                Put them all back
              </Button>
            )}
            {canAiExtract && (
              <Button link color="blue" onClick={() => setIsAiExtractOpen(true)}>
                Extract from document
              </Button>
            )}
            {canAiExtract && pendingUnmatched.length > 0 && (
              <Button link color="blue" onClick={() => setIsAddUnmatchedOpen(true)}>
                Assign unmatched items
              </Button>
            )}
            <Tooltip message="Export QA">
              <span className="p-1 hover-shadow1 cursor-pointer rounded" onClick={onDownload}>
                <PrinterIcon className="w-5 h-5 text-shade-blue" />
              </span>
            </Tooltip>

            {/*
              The server parses the upload with node-xlsx, which reads .xls and .csv as well as
              .xlsx, so the picker accepts all three. Accepting only the .xlsx MIME type greyed the
              others out, and a budget saved in an older or plainer format could not even be chosen.
            */}
            <Tooltip message="Import Excel">
              <>
                <label className="p-1 hover-shadow1 cursor-pointer rounded" htmlFor="importExcel">
                  <ArrowUpTrayIcon className="w-5 h-5 text-shade-blue" />
                </label>

                <input
                  className="hidden"
                  id="importExcel"
                  type="file"
                  accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                  multiple={false}
                  ref={fileInputRef}
                  onChange={importExcel}
                />
              </>
            </Tooltip>

            <Tooltip message="Export Excel">
              <a
                href={`${appApiUrl}/loan/budget/exportExcel/${loanNumber}/${id}?token=${stateData.auth.token}`}
                download
                className="p-1 hover-shadow1 cursor-pointer rounded"
              >
                <ArrowDownTrayIcon className="w-5 h-5 text-shade-blue" />
              </a>
            </Tooltip>

            <Tooltip message="Export PDF">
              <a
                href={`${appApiUrl}/loan/budget/exportPdf/${loanNumber}/${id}?token=${stateData.auth.token}`}
                download
                className="p-1 hover-shadow1 cursor-pointer rounded"
              >
                <InboxArrowDownIcon className="w-5 h-5 text-shade-blue" />
              </a>
            </Tooltip>
          </div>
        </div>

        <div className="overflow-auto relative">
          <table className="table relative w-full text-sm text-center">
            <thead className="">
              <tr className="bg-gray-100">
                {headers.map((item, index) => {
                  return (
                    <th className="sticky top-0 z-10 bg-gray-100 py-3 px-2 border" key={index}>
                      <span>
                        <div dangerouslySetInnerHTML={{ __html: item }}></div>
                      </span>
                    </th>
                  )
                })}
              </tr>
            </thead>

            {budget && !isEmpty(total) && (
              <tbody>
                {renderBudgetTotal()}

                {budget.items.map((item, index) => {
                  const prevTotalIndex = totalIndex
                  totalIndex += visibleLineKeys(item).length
                  return renderBudgetItem(item, index, prevTotalIndex)
                })}

                {renderBudgetTotal()}
              </tbody>
            )}
          </table>

          {isDownloadTypeDialog && (
            <DownloadTypeDialog
              onSubmit={(type: 'pdf' | 'docx') => onExportQA(type)}
              onClose={() => showDownloadTypeDialog(false)}
            />
          )}
        </div>
      </div>
    )
  }, [
    id,
    loanNumber,
    budget,
    total,
    isEditing,
    isBorrower,
    servicingPipeline,
    isFunded,
    inputs.BathroomCount?.value,
    isDownloadTypeDialog,
    stateData.auth.token,
    canAiExtract,
    pendingUnmatched.length,
    showHidden,
    unusedLineCount,
    keptLineCount,
    hiddenLineCount,
    unusedLineKeys,
  ])

  const renderHistoryModal = () => {
    return (
      <Modal
        button={
          <div
            className="hover:underline text-shade-blue cursor-pointer flex items-center gap-1 text-[14px]"
            onClick={() => setShowHistoryModal(true)}
          >
            <ClockIcon className="h-4 w-4"></ClockIcon>
            History
          </div>
        }
        title={'History'}
        titleOkay=""
        isOpen={showHistoryModal}
        lastUpdatedAt={Date.now()}
        onClose={() => setShowHistoryModal(false)}
        onOk={() => {}}
      >
        <div className="max-w-[720px]">
          <table className={`text-sm text-left`}>
            <thead className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide bg-slate-50 border-b border-slate-200 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th scope="col" className={`px-4 py-2`}>
                  No
                </th>
                <th scope="col" className={`px-4 py-2`}>
                  Date
                </th>
                <th scope="col" className={`px-4 py-2`}>
                  Action
                </th>
                <th scope="col" className={`px-4 py-2`}>
                  By
                </th>
              </tr>
            </thead>
            <tbody className="text-[14.5px] text-gray-900">
              {histories.map((item: any, index: number) => {
                return (
                  <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className={`px-4 py-2`}>{index + 1}</td>
                    <td className={`px-4 py-2`}>{formatTime(item.time)}</td>
                    <td className={`px-4 py-2`}>
                      <span>
                        <div className="flex gap-2">
                          {item.action}
                          {item.action === 'Borrower Submission' && (
                            <span className="cursor-pointer" onClick={() => openS3Document(item.pdfKey)}>
                              <EyeIcon className="w-4 h-4"></EyeIcon>
                            </span>
                          )}
                        </div>
                      </span>
                    </td>
                    <td className={`px-4 py-2`}>{item.email}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Modal>
    )
  }

  const renderSubmitModal = () => {
    return (
      <Modal
        button={<span></span>}
        title={'Submit Budget & Scope of Work'}
        titleOkay="Sign Scope of Work"
        isOpen={submitModal}
        lastUpdatedAt={Date.now()}
        onClose={() => setSubmitModal(false)}
        onOk={onConfirmAndSubmit}
        disabled={errors.length > 0}
        loading={loading}
      >
        <div className="max-w-[720px]">
          <div className="text-left">
            {errors.length > 0 && (
              <div
                className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4 text-[14px]"
                role="alert"
              >
                <div className="mb-2 font-semibold">Please provide the required information:</div>
                {errors.map((error: any, index: number) => {
                  return (
                    <div className="md:col-span-1" key={index}>
                      <div>- {error.title}</div>
                      {error.note && <div className="ml-3 border border-gray-200 p-2 italic mb-4">{error.note}</div>}
                    </div>
                  )
                })}
              </div>
            )}
            <div>
              After submitting this information, you <span className="font-bold">cannot edit</span> it anymore, so
              please ensure everything is entered correctly.
            </div>
            <div className="flex mt-4 text-[14px] font-semibold gap-2 items-center w-fit hover:underline cursor-pointer text-shade-blue">
              <span>
                <a
                  href={`${appApiUrl}/loan/budget/exportPdf/${loanNumber}/${id}?token=${stateData.auth.token}`}
                  download
                >
                  View PDF of Scope of Work
                </a>
              </span>
              <EyeIcon className="w-4 h-4"></EyeIcon>
            </div>
          </div>
        </div>
      </Modal>
    )
  }

  const fullName = (data: Record<string, string>) => [data.firstName, data.middleName, data.lastName].join(' ')
  const signData = useMemo(() => {
    const rtl = []
    const signature = <p className="font-['freehand'] text-[24px]">{fullName(borrower.borrower)}</p>
    const lastItem = histories[histories.length - 1]
    let status =
      lastItem?.action === 'Lock All Items'
        ? 'Signed'
        : (lastItem?.action as string)?.startsWith('Send Emails To:')
        ? 'Pending'
        : 'Unsigned'

    if (budget?.override) status = 'Signature Overridden'

    rtl.push([
      'Borrower',
      borrower.borrower.email,
      signature,
      <span className="text-shade-blue font-bold">{status}</span>,
      status === 'Signed' && lastItem?.time ? formatTime(lastItem?.time) : '',
      <RenderInput
        input={{
          inputType: 'checkbox',
          title: '',
          value: budget?.override,
          disabled: !isAdmin,
        }}
        Key={'override'}
        onChange={onChange}
        onBlur={onBlur}
      />,
    ])

    return rtl
  }, [histories, budget])

  return (
    <>
      <LayoutLoading show={loading} />

      {!isBorrower && (
        <PlainTable header={['', 'Email', 'Signature', 'Status', 'Signed Date', 'Override']} data={signData} />
      )}

      {!isBorrower && Object.keys(templateOptions).length > 1 && (
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="text-sm text-gray-600">Budget Template</span>
          <div className="w-[280px]">
            <Select
              id="budget-template-key"
              title=""
              options={templateOptions}
              value={templateKey}
              disabled={!alwaysEditable}
              onChange={onChangeTemplate}
            />
          </div>
        </div>
      )}

      {!isBorrower && (
        <div className="flex flex-wrap justify-between items-center gap-4 mb-2">
          <div className="flex flex-wrap items-center gap-4">
            <Button onClick={onSendToBorrower}>
              <div className="flex items-center">
                <EnvelopeIcon className="h-5 w-5 mr-1" />
                <span>Send to Parties</span>
              </div>
            </Button>
            <Button onClick={onOrderSitewire} loading={orderingSitewire}>
              {sitewire?.propertyId ? 'Update Sitewire' : 'Order Sitewire'}
            </Button>
            {sitewire?.propertyId && (
              <span className="text-sm text-gray-600">
                Property {sitewire.propertyId}
                {sitewire.orderedAt ? ` · Ordered ${formatTime(sitewire.orderedAt)}` : ''}
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-4 items-center">{alwaysEditable && renderHistoryModal()}</div>
        </div>
      )}

      {renderInputs()}
      <div
        className={`${
          budget?.narrativeNote?.length
            ? 'bg-yellow-100  border-yellow-400 text-yellow-700'
            : 'bg-gray-100  border-gray-400 text-gray-700'
        } border px-4 py-3 rounded relative text-[16px] mt-6`}
        role="alert"
      >
        <div className="flex gap-4 items-center mb-1">
          <span>
            - Please provide a narrative of what is transpiring in the project. Include details such as what exact work
            is being performed inside and outside of the structure. Is square footage being added? Will room count
            change? Etc.
          </span>
          {!isBorrower && (
            <>
              <span
                className="p-[4px] rounded hover-shadow1 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  showHistory('narrative')
                }}
              >
                <ClockIcon className="w-4 h-4"></ClockIcon>
              </span>
              <span
                className="p-[4px] rounded hover-shadow1 cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation()
                  editNarrativeNote()
                }}
              >
                <PencilSquareIcon className="w-4 h-4"></PencilSquareIcon>
              </span>
            </>
          )}
        </div>
        {budget?.narrativeNote?.length ? <div className="ml-2 mb-1 italic">{budget.narrativeNote}</div> : null}
        <RenderInput
          input={{ ...inputs['narrative'] }}
          Key={'narrative'}
          onChange={onChange}
          onBlur={() => onBlur('narrative')}
        />
      </div>

      {renderBudget()}

      {isBorrower && (
        <div className="flex justify-center mt-6">
          <Button disabled={!submitForm} onClick={() => setSubmitModal(true)}>
            <div className="flex gap-2">
              <svg className="animate-bounce w-6 h-6">
                <ArrowDownCircleIcon className="w-5 h-5"></ArrowDownCircleIcon>
              </svg>
              Submit Form
            </div>
          </Button>
        </div>
      )}
      {isBorrower && submitForm && <SaveChanges show={true} label="Submit Form" onSave={() => setSubmitModal(true)} />}

      {renderSubmitModal()}
      {isBorrowerEmailDialog && <BorroserEmailDialog onClose={onBorrowerEmailDialogClose} />}
      {showDrawRequests && <DrawRequestsDialog {...showDrawRequests} onClose={() => setShowDrawRequests(null)} />}
      {isAiExtractOpen && (
        <BudgetAiExtractModal
          isOpen={isAiExtractOpen}
          loanNumber={Number(loanNumber)}
          budgetId={id}
          onClose={() => setIsAiExtractOpen(false)}
          onApply={applyAiExtract}
          onAddUnmatched={openAddUnmatchedItems}
        />
      )}
      {isAddUnmatchedOpen && pendingUnmatched.length > 0 && budget && (
        <AddUnmatchedBudgetItemsModal
          unmatched={pendingUnmatched}
          budget={budget}
          occupiedLineKeys={occupiedLineKeys}
          onClose={(saved) => {
            setIsAddUnmatchedOpen(false)
            if (saved) {
              setPendingUnmatched([])
              setOccupiedLineKeys([])
            }
          }}
          onAssign={applyUnmatchedAssignments}
        />
      )}
    </>
  )
}

export default BudgetScopeOfWork
